/**
 * Lazy loaders for the two heavyweight vision libraries the capture screen
 * needs: OpenCV.js (~9 MB, drives auto-crop) and Tesseract.js (~10 MB of
 * language data, drives card-back OCR).
 *
 * Neither is bundled. They are ~20 MB combined, they are only reachable from
 * one screen, and a collector who never crops or OCRs should never pay for
 * them — so both are fetched from a CDN on first use and cached by the browser
 * thereafter. That is also why capture itself depends on neither: photographing
 * a card works on a dead connection; enhancing it does not.
 */

const OPENCV_SRC = 'https://docs.opencv.org/4.10.0/opencv.js';
const TESSERACT_SRC = 'https://unpkg.com/tesseract.js@5/dist/tesseract.min.js';

type Loader = Promise<void>;

const inflight = new Map<string, Loader>();

/** Inject a <script> once, resolving when it loads. Repeat calls share it. */
function loadScript(src: string): Loader {
  const existing = inflight.get(src);
  if (existing) return existing;

  const p = new Promise<void>((resolve, reject) => {
    const el = document.createElement('script');
    el.src = src;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => {
      // Drop the memo so a later retry (e.g. after reconnecting) can succeed
      // rather than replaying a cached rejection forever.
      inflight.delete(src);
      reject(new Error(`Could not load ${src}`));
    };
    document.head.appendChild(el);
  });

  inflight.set(src, p);
  return p;
}

/**
 * OpenCV.js is not usable the moment its script tag fires onload — the wasm
 * runtime initialises afterwards and `cv.Mat` appears only then. Poll for it.
 */
export async function ensureOpenCv(timeoutMs = 30000): Promise<boolean> {
  const w = window as any;
  if (w.cv?.Mat) return true;

  try {
    await loadScript(OPENCV_SRC);
  } catch {
    return false;
  }

  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (w.cv?.Mat) return true;
    await new Promise((r) => setTimeout(r, 100));
  }
  return false;
}

export async function ensureTesseract(): Promise<boolean> {
  const w = window as any;
  if (w.Tesseract) return true;
  try {
    await loadScript(TESSERACT_SRC);
  } catch {
    return false;
  }
  return Boolean(w.Tesseract);
}
